'use client';

import {
  ChevronLeftRounded,
  ChevronRightRounded,
  FormatQuoteRounded,
  StarRounded,
} from '@mui/icons-material';
import { Box, Container, IconButton, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useMemo, useState } from 'react';

import { StrapiImage } from '@/components/Utility/StrapiImage';
import { getVisibleCustomerReviews, normaliseRating } from '@/lib/helpers/reviewHelpers';
import { ComponentDataHomepageContent, Review } from '@/types';

import { newHomepageTypography } from './newHomepageStyles';

interface TestimonialsSectionProps {
  reviews: Review[];
  cms?: ComponentDataHomepageContent | null;
}

const fallbackReviews: Review[] = [
  {
    documentId: 'homepage-testimonial-fallback',
    Active: true,
    Author: 'Dean S.',
    Role: 'Managing Director',
    CompanyName: 'Anytime Fitness',
    Rating: 5,
    ReviewText:
      'I needed $100k for a gym fit-out fast. Lend.com.au got me approved and funded in 24 hours. Unbeatable speed.',
  } as Review,
];

function getInitials(name?: string | null) {
  if (!name) return '';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

export function TestimonialsSection({ reviews, cms }: TestimonialsSectionProps) {
  const theme = useTheme();

  const sectionLabel = cms?.TstSectionLabel || 'Reviews';
  const headingMain = cms?.TstHeadingMain || 'What others';
  const headingAccent = cms?.TstHeadingAccent || 'say';
  const quoteText = cms?.TstQuoteText || 'Real customers.\nReal experiences.';
  const activeReviews = useMemo(() => {
    const cmsReviews = getVisibleCustomerReviews(reviews);
    return cmsReviews.length > 0 ? cmsReviews : fallbackReviews;
  }, [reviews]);
  const [activeIndex, setActiveIndex] = useState(0);

  if (activeReviews.length === 0) return null;

  // Clamp in case the list shrank after activeIndex was set (keeps the
  // "{n} / {total}" counter and the displayed review in sync).
  const safeIndex = Math.min(activeIndex, activeReviews.length - 1);
  const review = activeReviews[safeIndex] ?? activeReviews[0];
  const rating = normaliseRating(review.Rating);
  const authorLine = [review.Role, review.CompanyName].filter(Boolean).join(', ');
  const canNavigate = activeReviews.length > 1;

  const goToPrevious = () => {
    setActiveIndex((current) => (current === 0 ? activeReviews.length - 1 : current - 1));
  };

  const goToNext = () => {
    setActiveIndex((current) => (current + 1) % activeReviews.length);
  };

  return (
    <Box
      component="section"
      sx={{ background: theme.palette.common.white, px: { xs: 2, md: 0 }, py: { xs: 7, md: 10 } }}
    >
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 7 } }}>
          <Typography sx={{ ...newHomepageTypography.sectionLabel, mb: 1.5 }}>
            {sectionLabel}
          </Typography>
          <Typography
            component="h2"
            sx={{ color: theme.palette.brand.bodyText, ...newHomepageTypography.h2 }}
          >
            <Box component="span" sx={{ color: theme.palette.brand.primary }}>
              {headingMain}
            </Box>{' '}
            {headingAccent}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.55fr) minmax(320px, 0.95fr)' },
            gap: { xs: 2, md: 3 },
            alignItems: 'stretch',
            width: '100%',
            mx: 'auto',
          }}
        >
          <Box
            sx={{
              minHeight: { xs: 230, md: 220 },
              borderRadius: 2,
              background: '#f7f7f9',
              px: { xs: 2.4, md: 3 },
              py: { xs: 2.6, md: 3 },
              display: 'flex',
              flexDirection: 'column',
              boxShadow: `0 18px 50px ${alpha(theme.palette.brand.primaryDark, 0.06)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, mb: 2.2 }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <StarRounded
                  key={index}
                  sx={{
                    fontSize: 15,
                    color:
                      index < Math.round(rating)
                        ? '#facc15'
                        : alpha(theme.palette.brand.bodyText, 0.14),
                  }}
                />
              ))}
              <Typography
                sx={{ ml: 0.6, color: alpha(theme.palette.brand.bodyText, 0.52), fontSize: 10 }}
              >
                {rating.toFixed(1)}
              </Typography>
            </Box>

            <Typography
              sx={{
                color: 'brand.bodyText',
                ...newHomepageTypography.body16,
                maxWidth: 466,
              }}
            >
              {review.ReviewText}
            </Typography>

            <Box sx={{ mt: 'auto', pt: 4, display: 'flex', alignItems: 'center', gap: 1.3 }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  flexShrink: 0,
                  display: 'grid',
                  placeItems: 'center',
                  background: theme.palette.gradients.gradient1,
                  color: theme.palette.common.white,
                  fontSize: 12,
                  fontWeight: 900,
                  boxShadow: `0 8px 18px ${alpha(theme.palette.brand.primary, 0.16)}`,
                  '& img': {
                    width: '100% !important',
                    height: '100% !important',
                    objectFit: 'cover',
                    display: 'block',
                  },
                }}
              >
                {review.Avatar ? (
                  <StrapiImage
                    image={review.Avatar}
                    size="thumbnail"
                    imageWidth={38}
                    imageHeight={38}
                    fixAxis="none"
                  />
                ) : (
                  getInitials(review.Author)
                )}
              </Box>
              <Box>
                <Typography sx={{ color: 'brand.bodyText', ...newHomepageTypography.meta14 }}>
                  {review.Author}
                </Typography>
                {authorLine && (
                  <Typography
                    sx={{
                      color: 'brand.bodyText',
                      ...newHomepageTypography.meta14,
                    }}
                  >
                    {authorLine}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                mt: { xs: 2.5, md: -1.5 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1.4,
              }}
            >
              <IconButton
                aria-label="Previous review"
                disabled={!canNavigate}
                onClick={goToPrevious}
                sx={{
                  width: 28,
                  height: 28,
                  color: alpha(theme.palette.brand.bodyText, 0.38),
                  '&:hover': {
                    color: theme.palette.brand.primary,
                    background: alpha(theme.palette.common.white, 0.8),
                  },
                }}
              >
                <ChevronLeftRounded sx={{ fontSize: 21 }} />
              </IconButton>
              <Typography
                sx={{ color: theme.palette.brand.bodyText, fontSize: 11, fontWeight: 900 }}
              >
                {safeIndex + 1} / {activeReviews.length}
              </Typography>
              <IconButton
                aria-label="Next review"
                disabled={!canNavigate}
                onClick={goToNext}
                sx={{
                  width: 28,
                  height: 28,
                  color: theme.palette.brand.bodyText,
                  '&:hover': {
                    color: theme.palette.brand.primary,
                    background: alpha(theme.palette.common.white, 0.8),
                  },
                }}
              >
                <ChevronRightRounded sx={{ fontSize: 21 }} />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              minHeight: { xs: 190, md: 220 },
              borderRadius: 2,
              background: theme.palette.brand.primary,
              color: theme.palette.common.white,
              px: { xs: 2.4, md: 3 },
              py: { xs: 3, md: 3.4 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxShadow: `0 20px 50px ${alpha(theme.palette.brand.primary, 0.18)}`,
            }}
          >
            <FormatQuoteRounded
              sx={{ fontSize: 36, color: alpha(theme.palette.common.white, 0.55), mb: 2 }}
            />
            <Typography
              sx={{
                width: '100%',
                maxWidth: 346,
                color: theme.palette.common.white,
                ...newHomepageTypography.h4Tight,
                letterSpacing: 0,
              }}
            >
              {quoteText.split('\n').map((line, i, arr) => (
                <span key={i}>
                  {line}
                  {i < arr.length - 1 && <br />}
                </span>
              ))}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
